/* =====================================================================
   sts-ui.js — window.STS.ui, the behaviour half of the v4 component layer.
   Spec: docs/new-theme-v4/specs/08-design-system-mobile-spec.md §3.6, §6.5.
   Pairs with assets/sts-components.css. Load with `defer`. No dependencies,
   ES2019, safe to load on any template.

   window.STS is shared: WS02 owns STS.state, this file owns STS.ui only and
   never reads STS.state — a page passes in what a component needs.

     STS.ui.open(target, opts)     opts {variant, returnFocus, focus} -> Promise
     STS.ui.close(target)          -> Promise, resolves after the close transition
     STS.ui.toast(message, opts)   defined in assets/sts-popups.js since v5.1
                                   (handoff 1.5 keeps ONE toast, print blocked)
     STS.ui.popover(trigger)       hover+focus on fine pointers, tap with an x on touch
     STS.ui.accordion(root, opts)  opts {single:false}
     STS.ui.tabs(root)             roving tabindex, arrows/Home/End
     STS.ui.scroller(root)         snap + desktop arrows
     STS.ui.stickyBar(bar, opts)   opts {watch:Element} -> reveals once watch leaves view
     STS.ui.slider(root, opts)     opts {interval, pauseOnHover, pauseControl}
     STS.ui.progress(node, value, max)   keeps aria-valuenow and the fill in step
     STS.ui.init(root)             wires every data-sts-* hook inside root

   Events, on the target element, bubbling: sts:open, sts:close.

   Accessibility contract every overlay here meets (AC-19): focus trap, focus
   return to the trigger, Escape, backdrop click, aria-modal, inert background,
   page scroll lock (STS.scroll, assets/sts-scroll.js: overflow:hidden on
   <html>, so the page never moves, and the tapped spot is held on close).
   ===================================================================== */
(function (w, d) {
  'use strict';
  var STS = (w.STS = w.STS || {});
  if (STS.ui) return;

  var FOCUSABLE = 'a[href],area[href],button:not([disabled]),' +
    'input:not([disabled]):not([type="hidden"]),select:not([disabled]),' +
    'textarea:not([disabled]),iframe,object,embed,summary,audio[controls],' +
    'video[controls],[contenteditable]:not([contenteditable="false"]),' +
    '[tabindex]:not([tabindex^="-"])';
  var DUR = 200;

  function reduced() {
    return !!(w.matchMedia && w.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }
  function fine() {
    return !!(w.matchMedia && w.matchMedia('(hover: hover) and (pointer: fine)').matches);
  }
  function el(t) {
    if (!t) return null;
    if (typeof t === 'string') return d.getElementById(t.charAt(0) === '#' ? t.slice(1) : t);
    return t;
  }
  function all(root, sel) { return [].slice.call((root || d).querySelectorAll(sel)); }
  function emit(node, name, detail) {
    node.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: detail || {} }));
  }
  function shown(n) {
    return !!(n.offsetWidth || n.offsetHeight || n.getClientRects().length);
  }
  function focusables(root) { return all(root, FOCUSABLE).filter(shown); }

  /* ---- scroll lock ----------------------------------------------------
     The site's one lock and the spot a closed overlay gives back are
     assets/sts-scroll.js (v6.2 §1.1-1.2). The class alone is the fallback
     should that file fail to load. */
  var locks = 0;
  function lockScroll() {
    if (STS.scroll) return STS.scroll.lock();
    if (!locks++) d.documentElement.classList.add('sts-locked');
  }
  function unlockScroll() {
    if (STS.scroll) return STS.scroll.unlock();
    if (locks && !--locks) d.documentElement.classList.remove('sts-locked');
  }

  /* ---- inert background ---------------------------------------------
     Walks from the overlay up to <body> and inerts every sibling on the way,
     so an overlay nested inside a section still hides the whole rest of the
     page from the tab order and the accessibility tree. */
  var canInert = 'inert' in HTMLElement.prototype;

  /* The fallback half of inertOutside, for an engine with no `inert`.
     aria-hidden="true" on its own is the worst of both worlds: the background
     is announced as gone while every link and button in it is still in the tab
     order, so Tab walked a screen-reader user through controls their own
     software had just told them did not exist. (The Tab trap in open() is bound
     to the overlay node, so it only helps while focus is already inside it — a
     stray click into the background escaped it.) Removing them from the tab
     order as well is what `inert` would have done. The previous tabindex is
     stashed on the element so an author's own tabindex survives the round trip. */
  var TABBABLE = 'a[href],button,input,select,textarea,details,summary,iframe,' +
    'audio[controls],video[controls],[contenteditable],[tabindex]';
  function sweepTabbable(scope, on) {
    var list = [].slice.call(scope.querySelectorAll(TABBABLE));
    if (scope.matches && scope.matches(TABBABLE)) list.unshift(scope);
    for (var i = 0; i < list.length; i++) {
      var e = list[i];
      if (on) {
        if (e.hasAttribute('data-sts-ti-was')) continue;
        e.setAttribute('data-sts-ti-was', e.hasAttribute('tabindex') ? e.getAttribute('tabindex') : '');
        e.setAttribute('tabindex', '-1');
      } else if (e.hasAttribute('data-sts-ti-was')) {
        var ti = e.getAttribute('data-sts-ti-was');
        e.removeAttribute('data-sts-ti-was');
        if (ti) e.setAttribute('tabindex', ti); else e.removeAttribute('tabindex');
      }
    }
  }

  function inertOutside(node, on) {
    var n = node;
    while (n && n !== d.body && n.parentNode) {
      var sibs = n.parentNode.children, i, s;
      for (i = 0; i < sibs.length; i++) {
        s = sibs[i];
        if (s === n || s.tagName === 'SCRIPT' || s.tagName === 'STYLE' || s.tagName === 'LINK') continue;
        if (on) {
          if (s.hasAttribute('data-sts-inert-was')) continue;
          s.setAttribute('data-sts-inert-was', canInert ? (s.inert ? '1' : '0') : (s.getAttribute('aria-hidden') || ''));
          if (canInert) s.inert = true;
          else { s.setAttribute('aria-hidden', 'true'); sweepTabbable(s, true); }
        } else if (s.hasAttribute('data-sts-inert-was')) {
          var was = s.getAttribute('data-sts-inert-was');
          s.removeAttribute('data-sts-inert-was');
          if (canInert) s.inert = was === '1';
          else {
            if (was) s.setAttribute('aria-hidden', was); else s.removeAttribute('aria-hidden');
            sweepTabbable(s, false);
          }
        }
      }
      n = n.parentNode;
    }
  }

  /* ---- overlays ------------------------------------------------------ */
  function panelOf(node) { return node.querySelector('.sts-overlay__panel') || node; }
  /* composedPath() is fixed when the event is dispatched, so it still holds
     the panel after a handler has swapped the target out of the DOM. */
  function inPanel(e, p) { var a = e.composedPath && e.composedPath(); return a ? a.indexOf(p) > -1 : p.contains(e.target); }

  function open(target, opts) {
    var node = el(target);
    if (!node) return Promise.resolve(null);
    if (node.__sts) return Promise.resolve(node);
    opts = opts || {};

    if (opts.variant && node.className.indexOf('sts-overlay--' + opts.variant) < 0) {
      node.classList.add('sts-overlay', 'sts-overlay--' + opts.variant);
    }

    var rec = {
      panel: panelOf(node),
      back: opts.returnFocus || d.activeElement,
      native: node.tagName === 'DIALOG' && typeof node.showModal === 'function'
    };
    node.__sts = rec;

    node.hidden = false;
    if (rec.native) {
      try { node.showModal(); } catch (e) { rec.native = false; }
    }
    if (!rec.native) {
      node.classList.add('is-open');
      if (!node.getAttribute('role')) node.setAttribute('role', 'dialog');
    }
    node.setAttribute('aria-modal', 'true');
    node.classList.remove('is-closing');

    lockScroll();
    inertOutside(node, true);

    // Escape. A native <dialog> raises `cancel`; we take it over so the close
    // transition and the focus return always run through one path.
    rec.onCancel = function (e) { e.preventDefault(); close(node); };
    rec.onKey = function (e) {
      if (e.key === 'Escape' || e.keyCode === 27) { e.preventDefault(); close(node); return; }
      if (e.key !== 'Tab' && e.keyCode !== 9) return;
      var f = focusables(rec.panel);
      if (!f.length) { e.preventDefault(); rec.panel.focus(); return; }
      var first = f[0], last = f[f.length - 1], a = d.activeElement;
      if (e.shiftKey && (a === first || !rec.panel.contains(a))) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && a === last) { e.preventDefault(); first.focus(); }
    };
    /* Backdrop, judged by inPanel(), not panel.contains(target): a control
       its own click handler redraws (My subscription's calendar days) is
       detached by the time the click bubbles here, and contains() closed the
       popup on it (QA on #642). A press that started in the panel (a drag
       let go over the scrim) is not a backdrop click either. */
    rec.onDown = function (e) { rec.down = inPanel(e, rec.panel); };
    rec.onClick = function (e) {
      if (e.target.closest && e.target.closest('[data-sts-close]')) { e.preventDefault(); close(node); return; }
      if (!rec.down && !inPanel(e, rec.panel)) close(node);   // backdrop
      rec.down = false;
    };
    node.addEventListener('cancel', rec.onCancel);
    node.addEventListener('keydown', rec.onKey);
    node.addEventListener('pointerdown', rec.onDown);
    node.addEventListener('click', rec.onClick);

    var target0 = el(opts.focus) || rec.panel.querySelector('[autofocus]') || focusables(rec.panel)[0];
    if (target0) { try { target0.focus(); } catch (e2) {} }
    else { rec.panel.setAttribute('tabindex', '-1'); rec.panel.focus(); }

    emit(node, 'sts:open', { variant: opts.variant || null });
    return Promise.resolve(node);
  }

  function close(target) {
    var node = el(target), rec = node && node.__sts;
    if (!rec) return Promise.resolve(null);
    node.__sts = null;
    node.removeEventListener('cancel', rec.onCancel);
    node.removeEventListener('keydown', rec.onKey);
    node.removeEventListener('pointerdown', rec.onDown);
    node.removeEventListener('click', rec.onClick);
    node.classList.add('is-closing');

    /* Everything that makes the overlay BLOCK the page happens now, in the same
       tick as the click that asked for the close; only the fade is allowed to
       outlive it. It used to all sit inside the timer below, which meant that
       for the length of the fade a native <dialog> was still open -- holding the
       top layer and keeping the whole document inert -- behind a scrim at
       opacity 0 whose own click and key handlers had just been unbound. Every
       click in that window went nowhere: no target, no close, no scroll. The
       shape of the bug is why it must not be a timer or a transitionend (which
       a dropped frame or a display change can skip outright) -- the page has to
       be live again whether or not the fade ever finishes.
       .is-closing keeps the element painted; see assets/sts-components.css. */
    if (rec.native) { try { node.close(); } catch (e) {} }
    else { node.classList.remove('is-open'); }
    node.removeAttribute('aria-modal');
    inertOutside(node, false);
    unlockScroll();
    if (rec.back && rec.back.focus) { try { rec.back.focus(); } catch (e2) {} }

    return new Promise(function (done) {
      setTimeout(function () {
        /* Reopened while it was fading: open() cleared .is-closing and set
           __sts again, so this timer is stale and must not hide it. */
        if (node.__sts) { done(node); return; }
        node.classList.remove('is-closing');
        /* Only the fallback path: a <dialog> is hidden by :not([open]) once the
           class goes, and hidden=true on one would stop the next showModal(). */
        if (!rec.native) node.hidden = true;
        emit(node, 'sts:close', {});
        done(node);
      }, reduced() ? 0 : DUR);
    });
  }

  /* ---- toast --------------------------------------------------------------
     Moved to assets/sts-popups.js with the popups that replaced it: v5.1
     handoff 1.5 keeps a single toast (print blocked), and this file has a
     5 KB served cap (spec 08 AC-29) that the move keeps it under. */

  /* ---- tooltip / info popover ----------------------------------------
     Fine pointers get hover and focus from CSS alone, so the tooltip works
     with JS switched off. This adds the touch behaviour (tap to open, x to
     close), Escape, outside-click, and keyboard focus on touch devices. */
  function popover(trigger) {
    trigger = el(trigger);
    if (!trigger || trigger.__stsPop) return;
    var wrap = trigger.closest('.sts-tipwrap') || trigger.parentNode;
    var id = trigger.getAttribute('aria-controls');
    var bubble = (id && d.getElementById(id)) || (wrap && wrap.querySelector('.sts-tip, .sts-popover'));
    if (!bubble) return;
    trigger.__stsPop = 1;

    if (!bubble.id) bubble.id = 'sts-tip-' + Math.random().toString(36).slice(2, 8);
    if (bubble.getAttribute('role') === 'tooltip') trigger.setAttribute('aria-describedby', bubble.id);
    trigger.setAttribute('aria-controls', bubble.id);
    trigger.setAttribute('aria-expanded', 'false');

    function show() {
      wrap.classList.add('is-open');
      bubble.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      d.addEventListener('click', outside, true);
      d.addEventListener('keydown', esc);
    }
    function hide(back) {
      wrap.classList.remove('is-open');
      bubble.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
      d.removeEventListener('click', outside, true);
      d.removeEventListener('keydown', esc);
      if (back) { try { trigger.focus(); } catch (e) {} }
    }
    function outside(e) { if (!wrap.contains(e.target)) hide(false); }
    function esc(e) { if (e.key === 'Escape' || e.keyCode === 27) hide(true); }

    trigger.addEventListener('click', function (e) {
      e.preventDefault();
      if (wrap.classList.contains('is-open')) hide(false); else show();
    });
    // Keyboard on a touch device: CSS only reveals the note on devices that
    // hover, so without this a Bluetooth keyboard could never read it.
    trigger.addEventListener('focus', function () {
      if (fine()) return;
      var kb = true;
      try { kb = trigger.matches(':focus-visible'); } catch (e) {}
      if (kb) show();
    });
    trigger.addEventListener('blur', function (e) {
      if (!bubble.contains(e.relatedTarget)) hide(false);
    });
    var x = bubble.querySelector('.sts-tip__close');
    if (x) x.addEventListener('click', function () { hide(true); });
  }

  /* ---- accordion ------------------------------------------------------
     <details> does the work. This only adds single-open where a page asks
     for it. Height is never animated, so nothing reflows mid-transition. */
  function accordion(root, opts) {
    root = el(root);
    if (!root || root.__stsAcc) return;
    root.__stsAcc = 1;
    if (!(opts && opts.single) && root.getAttribute('data-sts-single') !== 'true') return;
    var items = all(root, 'details');
    items.forEach(function (item) {
      item.addEventListener('toggle', function () {
        if (!item.open) return;
        items.forEach(function (o) { if (o !== item) o.open = false; });
      });
    });
  }

  /* ---- tabs ----------------------------------------------------------- */
  function tabs(root) {
    root = el(root);
    if (!root || root.__stsTabs) return;
    root.__stsTabs = 1;
    var list = root.querySelector('[role="tablist"]') || root.querySelector('.sts-tabs__list');
    if (!list) return;
    list.setAttribute('role', 'tablist');
    var btns = all(list, '.sts-tabs__tab, [role="tab"]');
    if (!btns.length) return;

    function panelFor(b) { return d.getElementById(b.getAttribute('aria-controls') || ''); }
    function select(b, focusIt) {
      btns.forEach(function (o) {
        var on = o === b, p = panelFor(o);
        o.setAttribute('role', 'tab');
        o.setAttribute('aria-selected', on ? 'true' : 'false');
        o.tabIndex = on ? 0 : -1;
        if (p) { p.setAttribute('role', 'tabpanel'); p.hidden = !on; if (!p.hasAttribute('tabindex')) p.tabIndex = 0; }
      });
      if (focusIt) b.focus();
    }
    var current = btns.filter(function (b) { return b.getAttribute('aria-selected') === 'true'; })[0] || btns[0];
    select(current, false);

    btns.forEach(function (b, i) {
      b.addEventListener('click', function (e) { e.preventDefault(); select(b, false); });
      b.addEventListener('keydown', function (e) {
        var k = e.key, n = null;
        if (k === 'ArrowRight' || k === 'ArrowDown') n = btns[(i + 1) % btns.length];
        else if (k === 'ArrowLeft' || k === 'ArrowUp') n = btns[(i - 1 + btns.length) % btns.length];
        else if (k === 'Home') n = btns[0];
        else if (k === 'End') n = btns[btns.length - 1];
        if (!n) return;
        e.preventDefault();
        select(n, true);
      });
    });
  }

  /* ---- horizontal scroller -------------------------------------------- */
  function scroller(root) {
    root = el(root);
    if (!root || root.__stsScr) return;
    root.__stsScr = 1;
    var wrap = root.closest('.sts-scrollerwrap');
    if (!wrap) return;
    var prev = wrap.querySelector('.sts-scroller__nav--prev');
    var next = wrap.querySelector('.sts-scroller__nav--next');
    if (!prev && !next) return;
    if (!root.id) root.id = 'sts-scroller-' + Math.random().toString(36).slice(2, 8);
    [prev, next].forEach(function (b) { if (b) b.setAttribute('aria-controls', root.id); });

    function step() {
      var kid = root.firstElementChild;
      return kid ? kid.getBoundingClientRect().width + 12 : root.clientWidth * 0.8;
    }
    function go(dir) {
      root.scrollBy({ left: dir * step(), behavior: reduced() ? 'auto' : 'smooth' });
    }
    function sync() {
      var max = root.scrollWidth - root.clientWidth - 1;
      if (prev) prev.disabled = root.scrollLeft <= 0;
      if (next) next.disabled = root.scrollLeft >= max;
    }
    if (prev) prev.addEventListener('click', function () { go(-1); });
    if (next) next.addEventListener('click', function () { go(1); });
    root.addEventListener('scroll', sync, { passive: true });
    w.addEventListener('resize', sync);
    sync();
  }

  /* ---- sticky bar ------------------------------------------------------
     Shown only once `watch` has left the viewport (08-Q10), so the PDP's
     first paint keeps its 60px. With no watch element it shows immediately. */
  function stickyBar(bar, opts) {
    bar = el(bar);
    if (!bar || bar.__stsBar) return;
    bar.__stsBar = 1;
    opts = opts || {};
    var watch = el(opts.watch) || el(bar.getAttribute('data-sts-watch'));
    function show(on) { bar.classList.toggle('is-visible', !!on); }
    if (!watch || !('IntersectionObserver' in w)) { show(true); return; }
    new IntersectionObserver(function (entries) {
      show(!entries[0].isIntersecting);
    }, { threshold: 0 }).observe(watch);
  }

  /* ---- sticky header ---------------------------------------------------
     There is no stickyHeader() here. Spec 08 §3.6 lists one in this API, but
     the header that hides on scroll is WS03's and always was: assets/
     sts-chrome.js `hideOnScroll()`, gated on data-sts-sticky="hide_on_scroll"
     and driving data-hidden on [data-sts-header]. This copy was reached by no
     `data-sts-sticky-header` attribute anywhere in the theme, called by no
     module, and its CSS class .sts-stickyhead was written by no markup — a
     third implementation of one behaviour (assets/theme.js has the legacy
     one), costing bytes in the file with the least budget headroom.
     If a second sticky region ever needs it, take chrome's, do not re-add
     this one. */

  /* ---- slider ----------------------------------------------------------
     Pauses on hover, on focus within, off-screen and when the tab is hidden;
     never auto-advances under reduced motion; always has a visible pause
     control (WCAG 2.2.2). Slides stack with opacity so nothing shifts. */
  function slider(root, opts) {
    root = el(root);
    if (!root || root.__stsSlider) return;
    /* A section that ships its own slider script owns this root. The banner
       slider (sections/sts-banner-slider.liquid + assets/sts-home.js) uses the
       same [data-sts-slider] hook and the same .sts-slider__slide / __dot
       classes, so init() below used to wire it a SECOND time: two independent
       timers, aria-hidden="true" landing on whichever slide was actually
       visible, and — worst — tabIndex=-1 on every dot but the first, which took
       dots 2..n out of the tab order entirely. One driver per root. */
    if (root.hasAttribute('data-sts-slider-autoplay')) return;
    root.__stsSlider = 1;
    opts = opts || {};
    var slides = all(root, '.sts-slider__slide');
    if (slides.length < 2) return;
    var dots = all(root, '.sts-slider__dot');
    var pauseBtn = root.querySelector('.sts-slider__pause');
    var interval = +(opts.interval || root.getAttribute('data-sts-interval') || 5000);
    var i = Math.max(0, slides.findIndex(function (s) { return s.classList.contains('is-active'); }));
    var timer = null, paused = false, visible = true, userPaused = reduced();

    function render() {
      slides.forEach(function (s, n) {
        s.classList.toggle('is-active', n === i);
        s.setAttribute('aria-hidden', n === i ? 'false' : 'true');
        /* Same reason the dots get a roving tabindex below: a slide can hold
           links. aria-hidden takes them out of the accessibility tree and
           leaves them in the tab order, which is the one combination that is
           worse than doing nothing — .sts-slider__slide{visibility:hidden} in
           assets/sts-components.css was the only thing preventing it. */
        if (canInert) s.inert = n !== i;
      });
      dots.forEach(function (dt, n) {
        dt.setAttribute('aria-selected', n === i ? 'true' : 'false');
        dt.tabIndex = n === i ? 0 : -1;
      });
    }
    function tick() { i = (i + 1) % slides.length; render(); }
    function play() {
      stop();
      if (userPaused || paused || !visible || reduced()) return;
      timer = setInterval(tick, interval);
    }
    function stop() { if (timer) { clearInterval(timer); timer = null; } }
    function setPauseLabel() {
      if (!pauseBtn) return;
      var label = userPaused ? 'Play' : 'Pause';
      pauseBtn.setAttribute('aria-label', label);
      pauseBtn.setAttribute('aria-pressed', userPaused ? 'true' : 'false');
      var t = pauseBtn.querySelector('[data-sts-label]');
      if (t) t.textContent = label;
    }

    dots.forEach(function (dt, n) {
      dt.addEventListener('click', function () { i = n; render(); play(); });
    });
    if (pauseBtn) {
      pauseBtn.addEventListener('click', function () {
        userPaused = !userPaused;
        setPauseLabel();
        play();
      });
      setPauseLabel();
    }
    if (opts.pauseOnHover !== false) {
      root.addEventListener('mouseenter', function () { paused = true; stop(); });
      root.addEventListener('mouseleave', function () { paused = false; play(); });
    }
    root.addEventListener('focusin', function () { paused = true; stop(); });
    root.addEventListener('focusout', function (e) {
      if (!root.contains(e.relatedTarget)) { paused = false; play(); }
    });
    d.addEventListener('visibilitychange', function () {
      if (d.hidden) stop(); else play();
    });
    if ('IntersectionObserver' in w) {
      new IntersectionObserver(function (entries) {
        visible = entries[0].isIntersecting;
        play();
      }, { threshold: 0.2 }).observe(root);
    }
    render();
    play();
  }

  /* ---- progress --------------------------------------------------------
     One call keeps the fill, the ARIA value and the caption in step, so a
     progress bar can never say one thing visually and another to a reader. */
  function progress(node, value, max) {
    node = el(node);
    if (!node) return;
    max = max == null ? +(node.getAttribute('aria-valuemax') || 100) : max;
    value = Math.max(0, Math.min(+value || 0, max));
    var pct = max ? Math.round((value / max) * 100) : 0;
    node.setAttribute('role', 'progressbar');
    node.setAttribute('aria-valuemin', '0');
    node.setAttribute('aria-valuemax', String(max));
    node.setAttribute('aria-valuenow', String(value));
    node.style.setProperty('--sts-progress', String(pct));
    return pct;
  }

  /* ---- auto-wiring ------------------------------------------------------
     Markup opts in with data attributes so a section never needs its own
     script tag:
       <button data-sts-open="#filters">         opens that overlay
       <button data-sts-close>                   closes the overlay it sits in
       <div data-sts-tabs> <div data-sts-accordion data-sts-single="true">
       <div data-sts-scroller> <button data-sts-tip>
       <div data-sts-stickybar data-sts-watch="#main-atc">
       <div data-sts-slider data-sts-interval="5000">
  */
  function init(root) {
    root = el(root) || d;
    all(root, '[data-sts-open]').forEach(function (b) {
      if (b.__stsTrig) return;
      b.__stsTrig = 1;
      var sel = b.getAttribute('data-sts-open');
      var target = sel.charAt(0) === '#' ? d.querySelector(sel) : d.getElementById(sel);
      if (target) {
        if (!b.hasAttribute('aria-controls') && target.id) b.setAttribute('aria-controls', target.id);
        b.addEventListener('click', function (e) { e.preventDefault(); open(target, { returnFocus: b }); });
      }
    });
    all(root, '[data-sts-tabs]').forEach(tabs);
    all(root, '[data-sts-accordion]').forEach(function (n) { accordion(n); });
    all(root, '[data-sts-scroller]').forEach(scroller);
    all(root, '[data-sts-tip]').forEach(popover);
    all(root, '[data-sts-stickybar]').forEach(function (n) { stickyBar(n); });
    /* Own only the is-active/.sts-slider__pause/data-sts-interval convention
       (sections/sts-kitchen-sink.liquid). A root also carrying
       data-sts-slider-autoplay is assets/sts-home.js's initSlider() markup
       (sections/sts-banner-slider.liquid, which drives data-sts-slide-active
       instead) - skip it, or both scripts would run their own timer on the
       same .sts-slider__slide elements and force two slides to opacity:1
       at once. */
    all(root, '[data-sts-slider]:not([data-sts-slider-autoplay])').forEach(function (n) { slider(n); });
  }

  STS.ui = {
    open: open, close: close, popover: popover, accordion: accordion,
    tabs: tabs, scroller: scroller, stickyBar: stickyBar,
    slider: slider, progress: progress, init: init,
    lockScroll: lockScroll, unlockScroll: unlockScroll
  };

  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', function () { init(d); });
  else init(d);
  // Theme editor: a re-rendered section arrives with fresh, unwired markup.
  d.addEventListener('shopify:section:load', function (e) { init(e.target); });
})(window, document);

/* Product-card (i) reason popover (snippets/sts-product-card.liquid). Here, not in
   sts-collection.js, because article shortcode rows render cards without that file.
   Window CAPTURE phase: Boost binds result clicks to [data-product-id] cards and
   navigated before a bubbling handler ever ran. One click handler decides open,
   close or outside. Hover opens (not pinned); a click or tap pins; a second one or
   an outside click or Esc closes. Notes: asset-notes/sts-collection.md. */
(function (w, d) {
  'use strict';
  var cur = null, pinned = false;
  function set(b, open) {
    var card = b.closest('[data-sts-card]');
    b.setAttribute('aria-expanded', String(open));
    if (card) card.toggleAttribute('data-sts-why', open);
    cur = open ? b : null;
    pinned = false;
  }
  function btn(e) { return e.target && e.target.closest ? e.target.closest('[data-sts-why-btn]') : null; }
  ['pointerdown', 'pointerup', 'mousedown', 'mouseup', 'touchstart', 'touchend'].forEach(function (t) {
    w.addEventListener(t, function (e) { if (btn(e)) e.stopImmediatePropagation(); }, true);
  });
  w.addEventListener('click', function (e) {
    var b = btn(e);
    if (!b) {
      if (cur && !e.target.closest('.sts-card__why')) set(cur, false);
      return;
    }
    e.preventDefault();
    e.stopImmediatePropagation();
    if (cur && cur !== b) set(cur, false);
    if (cur === b && pinned) { set(b, false); return; }
    set(b, true);
    pinned = true;
  }, true);
  d.addEventListener('mouseover', function (e) {
    var b = btn(e);
    if (b && cur !== b && !pinned) { if (cur) set(cur, false); set(b, true); }
  });
  d.addEventListener('mouseout', function (e) {
    var b = btn(e);
    if (b && cur === b && !pinned && !b.contains(e.relatedTarget)) set(b, false);
  });
  d.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && cur) { var b = cur; set(b, false); b.focus(); }
  });
})(window, document);
